import React, { useMemo, Fragment } from "react";
import GridCell from "components/GridCell";
import { useFormikContext } from "formik";
import TemplateProgressControls from "components/TemplateBuilderForm/TemplateProgressControls";
import { entitiesForTemplate, reorderEntities } from "utils/entity";
import { useSelector } from "react-redux";
import Padder from "components/Padder";
import { TEMPLATE_PAGE_TYPE } from "components/FormerEditor/common/constants/entity";
import {
  TEMPLATE_ACTION,
  TEMPLATE_PAGE_TYPE_BUILDER_SECTIONS
} from "utils/constants/template";
import { getFlexTemplatePrice } from "utils/constants/price";
import { useCtxReferences, useManifests } from "utils/selectors";
import { isPlatformSubdomain } from "utils/subdomain";
import cloneDeep from "lodash/cloneDeep";
import set from "lodash/set";
import Tabs, { WHITE_TAB_STYLES } from "components/Tabs";
import { prepareManifestList } from "utils/manifest";
import ManifestOption from "components/ManifestOption";
import ModelSearchList from "components/ModelSearchList";
import { MANIFEST } from "utils/constants/models";
import { LIST_PRESENTATION, MODEL_SEARCH_CLASSES } from "utils/constants/ui";

const FORM_TEMPLATE = {
  uid: TEMPLATE_PAGE_TYPE.FORM,
  label: "Checkout form",
  description:
    "Build a checkout form to collect information and payment from your customers."
};

const STORE_TEMPLATE = {
  uid: TEMPLATE_PAGE_TYPE.STORE,
  label: "Store",
  description: "Build a store to sell one or more of your products."
};

const PRICING_TEMPLATE = {
  uid: TEMPLATE_PAGE_TYPE.PRICING,
  label: "Pricing",
  description: "Build a pricing page complete with a feature comparison table."
};

const TIP_TEMPLATE = {
  uid: TEMPLATE_PAGE_TYPE.TIP,
  label: "Tip",
  description:
    "Build a tip page so your supporters can contribute to your work."
};

const PAGE_TEMPLATES = [FORM_TEMPLATE];

const TEMPLATE_PATHS = {
  FORM_SECTIONS: "form.sections",
  FORM_TEMPLATE: "form.template",
  ENTITIES: "entities"
};

const PRICES_REQUIRED_TEMPLATES = {
  [TEMPLATE_PAGE_TYPE.PAYMENT]: true,
  [TEMPLATE_PAGE_TYPE.TIP]: true
};

const TEMPLATE = "template";
const COPY = "copy";
const BLOCK_PICKER_TABS = {
  TEMPLATE,
  COPY
};

const TemplateBlockPicker = ({ tracking }) => {
  const { values, setValues, setFieldValue } = useFormikContext();
  const { application } = useCtxReferences();

  const manifests = useManifests();

  /**
   * The copy manifest action requires a uuid
   * But the ManifestOption returns an id on selection
   * So we maintain two list to transfer between id and uuid references as needed
   */
  const { preparedManifests, manifestIDMap, manifestUUIDMap } = useMemo(() => {
    if (Array.isArray(manifests)) {
      return {
        ...manifests.reduce(
          (memo, manifest) => {
            memo.manifestIDMap[manifest.id] = manifest.uuid;
            memo.manifestUUIDMap[manifest.uuid] = manifest.id;
            return memo;
          },
          {
            manifestIDMap: {},
            manifestUUIDMap: {}
          }
        ),
        preparedManifests: prepareManifestList({
          manifests,
          application,
          shouldOrder: true
        })
      };
    } else {
      return {
        preparedManifests: [],
        manifestIDMap: {},
        manifestUUIDMap: {}
      };
    }
  }, [manifests.length]);

  const isPlatform = isPlatformSubdomain(application && application.subdomain);
  const appEntities = useSelector((state) =>
    state.getIn(["app", "data", "entities"]).toJS()
  );
  const { entities: allEntities } = values.form;
  const templateOptions = [...PAGE_TEMPLATES];

  if (isPlatform) {
    /**
     * Optional addition of template types
     */
    templateOptions.push(STORE_TEMPLATE);
    templateOptions.push(PRICING_TEMPLATE);
    templateOptions.push(TIP_TEMPLATE);
  }

  const templateOptionNodes = templateOptions.map((template, templateIx) => {
    const templateEntities = entitiesForTemplate(template.uid, allEntities);
    const isSelected = template.uid === values.form.template;

    return (
      <GridCell
        key={template.uid}
        id={template.uid}
        isFirst={!templateIx}
        isSelected={isSelected}
        isLast={allEntities.length - 1 === templateIx}
        {...template}
        customClasses={{
          container: "flex flex-column w-100 ba br2 pa3 mb3 relative"
        }}
        onClick={() => {
          const updatedValues = cloneDeep(values);
          const clickEntities = reorderEntities(appEntities, templateEntities);
          const newSections = TEMPLATE_PAGE_TYPE_BUILDER_SECTIONS[template.uid];
          set(updatedValues, TEMPLATE_PATHS.FORM_SECTIONS, newSections);
          set(updatedValues, TEMPLATE_PATHS.FORM_TEMPLATE, template.uid);
          set(updatedValues, TEMPLATE_PATHS.ENTITIES, clickEntities);

          const needsPrices = PRICES_REQUIRED_TEMPLATES[template.uid];
          const updatedPrices = needsPrices ? [getFlexTemplatePrice(500)] : [];
          set(updatedValues, "prices", updatedPrices);

          setValues(updatedValues);
        }}
      />
    );
  });
  const activeIds =
    values.form.copyManifestUUID &&
    manifestUUIDMap[values.form.copyManifestUUID]
      ? [manifestUUIDMap[values.form.copyManifestUUID]]
      : [];

  const manifestOptionNodes = (
    <ModelSearchList
      models={preparedManifests}
      query={{
        model: MANIFEST,
        keys: ["title", "previewUrl", "description"]
      }}
      placeholder="Search by page title or url"
      customClasses={{
        [LIST_PRESENTATION.GRID]: {
          ...MODEL_SEARCH_CLASSES,
          list: {
            list: "grid-container-2 ma0 pa0 list",
            item: "w-100"
          }
        },
        [LIST_PRESENTATION.LIST]: { ...MODEL_SEARCH_CLASSES }
      }}
      components={{
        [LIST_PRESENTATION.LIST]: ManifestOption
      }}
      activeModelIds={activeIds}
      onClick={(selection) => {
        const manifestUUID = manifestIDMap[selection];

        if (manifestUUID) {
          setFieldValue("form.copyManifestUUID", manifestUUID);
        } else {
          console.error(`No matching manifest for id:${selection}`);
        }
      }}
    />
  );
  const canCopyManifests = preparedManifests.length > 0;

  const title = canCopyManifests ? "Build a page" : "Choose a template";
  const body = isPlatform
    ? "Create a checkout form or single page store for one or more of your products."
    : "Create a checkout form for one or more of your products.";
  const footer = canCopyManifests
    ? "Pick a template to get started or copy one of your existing pages."
    : templateOptions.length
    ? "Pick the right page template for your use case."
    : "";

  const copyContent = (
    <div className="w-100 w-70-ns">
      <div className="f3 f2-ns fw6 pt3">{title}</div>
      <div className="f5 f3-ns pt3 lh-copy">{body}</div>
      {footer ? <div className="f5 f3-ns pt3 lh-copy">{footer}</div> : null}
    </div>
  );

  const tabProps = {
    minWidth: false,
    equalWidth: false,
    pathPrefix: "",
    customStyles: WHITE_TAB_STYLES,
    customClasses: {
      tabs: {
        list:
          "list ma0 pa0 nowrap center overflow-x-auto absolute top-0 left-0 right-0 ph3"
      }
    },
    activeTabID: values.form.action,
    onUpdate: (tabID) => {
      setFieldValue("form.action", tabID);
      const shouldResetCopyManifestUUID =
        tabID !== BLOCK_PICKER_TABS.COPY && values.form.copyManifestUUID;
      if (shouldResetCopyManifestUUID) {
        setFieldValue("form.copyManifestUUID", null);
      }
    },
    tabs: [
      {
        name: "Templates",
        id: TEMPLATE_ACTION.NEW,
        node: (
          <ul className="ma0 pa3 list bg-white pt3">
            {templateOptionNodes}
            <Padder theme="visible" />
          </ul>
        )
      }
    ]
  };
  if (canCopyManifests) {
    tabProps.tabs.push({
      name: "Copy",
      id: TEMPLATE_ACTION.COPY,
      node: (
        <ul className="ma0 pa0 list bg-white pt3">
          {manifestOptionNodes}
          <Padder theme="visible" />
        </ul>
      )
    });
  }

  const right = (
    <Fragment>
      <div
        style={{ top: 56 }}
        className="sticky top-0 pb3 bg-white bb hairline-1 z-6"
      >
        <TemplateProgressControls />
      </div>
      <div className="flex dn-ns bg-white flex-column w-100 w-50-ns pt2 pl3 pb3">
        {copyContent}
      </div>
      <div className="pt3">
        <Tabs {...tabProps} />
      </div>
    </Fragment>
  );

  return (
    <div className="w-100 flex flex-column flex-row-ns">
      <div className="dn flex-ns flex-column w-100 w-50-ns pt3 pl3 pt3 pa3-ns pa4-ns bg-content--secondary bh--inset min-vh-100-ns">
        {copyContent}
      </div>
      <div className="flex flex-column w-100 w-50-ns relative">{right}</div>
    </div>
  );
};

export default TemplateBlockPicker;
